import { useState, useEffect, useCallback, useMemo } from 'react'
import { teacherApi, TimeSlotResponse, RadarResponse, RadarConfigResponse } from '@/services/api/teacher'
import type { TimetableEntryResponse, SubstitutionRequestResponse, TeacherTimetableEntry } from '@/services/api/teacher'

export function useTeacherData() {
  const [timetable, setTimetable] = useState<{ entries: TimetableEntryResponse[]; week_start: string } | null>(null)
  const [timetableEntries, setTimetableEntries] = useState<TeacherTimetableEntry[]>([])
  const [isLoadingTimetableEntries, setIsLoadingTimetableEntries] = useState(false)
  const [schoolTimeSlots, setSchoolTimeSlots] = useState<TimeSlotResponse[]>([])
  const [requests, setRequests] = useState<SubstitutionRequestResponse[]>([])
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  // Radar state
  const [radarData, setRadarData] = useState<RadarResponse | null>(null)
  const [radarConfig, setRadarConfig] = useState<RadarConfigResponse | null>(null)
  const [timeRangeDays, setTimeRangeDays] = useState<number>(14)
  const [isLoadingRadar, setIsLoadingRadar] = useState(false)

  const fetchTimetable = useCallback(async () => {
    try {
      const res = await teacherApi.getMyTimetable()
      setTimetable(res)
    } catch (e: any) {
      console.error('[TeacherDashboard] Timetable fetch error:', {
        status: e?.statusCode,
        detail: e?.detail,
        response: e?.response?.data,
        message: e?.message,
      })
      setError(`Stundenplan konnte nicht geladen werden (${e?.statusCode || 'Unbekannt'})`)
    }
  }, [])

  const fetchTimetableEntries = useCallback(async () => {
    setIsLoadingTimetableEntries(true)
    try {
      const entries = await teacherApi.getMyTimetableEntries()
      setTimetableEntries(entries)
    } catch (e: any) {
      console.error('[TeacherDashboard] Timetable entries fetch error:', {
        status: e?.statusCode,
        detail: e?.detail,
        response: e?.response?.data,
        message: e?.message,
      })
    } finally {
      setIsLoadingTimetableEntries(false)
    }
  }, [])

  const fetchTimeSlots = useCallback(async () => {
    try {
      const slots = await teacherApi.getMyTimeSlots()
      setSchoolTimeSlots(slots)
    } catch (e: any) {
      console.error('[TeacherDashboard] Time slots fetch error:', {
        status: e?.statusCode,
        detail: e?.detail,
        response: e?.response?.data,
        message: e?.message,
      })
    }
  }, [])

  const fetchRequests = useCallback(async () => {
    try {
      const res = await teacherApi.getMySubstitutions()
      setRequests(res)
    } catch (e: any) {
      console.error('[TeacherDashboard] Substitutions fetch error:', {
        status: e?.statusCode,
        detail: e?.detail,
        response: e?.response?.data,
        message: e?.message,
      })
      setError(`Vertretungsanfragen konnten nicht geladen werden (${e?.statusCode || 'Unbekannt'})`)
    }
  }, [])

  const fetchRadarConfig = useCallback(async () => {
    try {
      const cfg = await teacherApi.getRadarConfig()
      setRadarConfig(cfg)
      setTimeRangeDays(cfg.default_time_range_days)
    } catch (e: any) {
      console.error('[TeacherDashboard] Radar config fetch error:', e)
    }
  }, [])

  const fetchRadarBubbles = useCallback(async () => {
    setIsLoadingRadar(true)
    try {
      const data = await teacherApi.getTeacherRadarBubbles(timeRangeDays)
      setRadarData(data)
    } catch (e: any) {
      console.error('[TeacherDashboard] Radar bubbles fetch error:', {
        status: e?.statusCode,
        detail: e?.detail,
        response: e?.response?.data,
        message: e?.message,
      })
      setError(`Radar konnte nicht geladen werden (${e?.statusCode || 'Unbekannt'})`)
    } finally {
      setIsLoadingRadar(false)
    }
  }, [timeRangeDays])

  const handleRefresh = useCallback(async () => {
    try {
      await teacherApi.refreshTimetable()
      setMessage('Stundenplan wurde aktualisiert')
      fetchTimetable()
      fetchTimetableEntries()
    } catch (e: any) {
      console.error('[TeacherDashboard] Timetable refresh error:', {
        status: e?.statusCode,
        detail: e?.detail,
        response: e?.response?.data,
      })
      setError(`Aktualisierung fehlgeschlagen (${e?.statusCode || 'Unbekannt'})`)
    }
  }, [fetchTimetable, fetchTimetableEntries])

  const handleDeleteRequest = useCallback(async (id: string) => {
    if (!confirm('Vertretungsanfrage wirklich löschen?')) return
    try {
      await teacherApi.deleteSubstitution(id)
      setMessage('Anfrage gelöscht')
      fetchRequests()
    } catch (e: any) {
      console.error('[TeacherDashboard] Substitution delete error:', {
        status: e?.statusCode,
        detail: e?.detail,
        response: e?.response?.data,
      })
      setError(e?.response?.data?.detail || `Fehler beim Löschen (${e?.statusCode || 'Unbekannt'})`)
    }
  }, [fetchRequests])

  const handleSendReminder = useCallback(async (lessonRequestId: string, customMessage?: string) => {
    try {
      const result = await teacherApi.sendRadarReminder([lessonRequestId], customMessage)
      setMessage(`Erinnerung gesendet an ${result.sent_count} Lehrperson(en)`)
      fetchRadarBubbles()
    } catch (e: any) {
      console.error('[TeacherDashboard] Reminder send error:', e)
      setError(e?.response?.data?.detail || 'Erinnerung senden fehlgeschlagen')
    }
  }, [fetchRadarBubbles])

  const handleSendMessage = useCallback(async (lessonRequestId: string, recipientIds: string[], messageText: string, includeDetails: boolean) => {
    try {
      const result = await teacherApi.sendRadarMessage([lessonRequestId], {
        recipient_teacher_ids: recipientIds,
        message_text: messageText,
        include_lesson_details: includeDetails,
      })
      setMessage(`Nachricht gesendet an ${result.sent_count} Lehrperson(en)`)
      fetchRadarBubbles()
    } catch (e: any) {
      console.error('[TeacherDashboard] Message send error:', e)
      setError(e?.response?.data?.detail || 'Nachricht senden fehlgeschlagen')
    }
  }, [fetchRadarBubbles])

  const handleTimeRangeChange = useCallback((newDays: number) => {
    setTimeRangeDays(newDays)
  }, [])

  useEffect(() => {
    fetchTimetable()
    fetchTimetableEntries()
    fetchTimeSlots()
    fetchRequests()
    fetchRadarConfig()
  }, [fetchTimetable, fetchTimetableEntries, fetchTimeSlots, fetchRequests, fetchRadarConfig])

  useEffect(() => {
    if (radarConfig) {
      fetchRadarBubbles()
    }
  }, [timeRangeDays, fetchRadarBubbles, radarConfig])

  const fixedTimeSlots = useMemo(() => {
    if (schoolTimeSlots.length > 0) {
      return schoolTimeSlots.map(ts => ts.name || `${ts.start_time}-${ts.end_time}`)
    }
    return undefined
  }, [schoolTimeSlots])

  const incomingRadarBubbles = useMemo(() => {
    return (requests || []).map(r => ({
      id: r.id,
      date: r.date || '',
      subject: r.subject_name || '',
      periods: 1,
      status: r.status === 'booked' || r.status === 'accepted' ? 'confirmed' as const :
              r.status === 'declined' ? 'declined' as const : 'open' as const,
      substituteName: r.substitute_teacher_name,
    }))
  }, [requests])

  return {
    timetable,
    timetableEntries,
    setTimetableEntries,
    isLoadingTimetableEntries,
    schoolTimeSlots,
    requests,
    setRequests,
    error,
    setError,
    message,
    setMessage,
    fixedTimeSlots,
    incomingRadarBubbles,
    fetchTimetable,
    fetchTimetableEntries,
    fetchTimeSlots,
    fetchRequests,
    handleRefresh,
    handleDeleteRequest,
    // Radar
    radarData,
    radarConfig,
    timeRangeDays,
    isLoadingRadar,
    fetchRadarBubbles,
    fetchRadarConfig,
    handleSendReminder,
    handleSendMessage,
    handleTimeRangeChange,
  }
}
